/**
 * @internal
 * Overlay layer used inside BarChart for brush/zoom. Not part of the public API.
 * Use `BarChart` for all bar chart needs.
 */
import React, { forwardRef, RefObject } from 'react';
import { scaleLinear as d3scaleLinear } from 'd3';

import { Comp } from '@redsift/design-system';

import { BarChartOverlayProps } from './types';
import { StyledBarChartOverlay } from './styles';
import { useBarChartContext } from './context';
import { BarDatum } from '../../types';
import { Bar, BarOrientation } from '../Bar';

const COMPONENT_NAME = 'BarChartOverlay';
const CLASSNAME = 'redsift-barchart-overlay';

export const BarChartOverlay: Comp<BarChartOverlayProps, SVGGElement> = forwardRef((props, ref) => {
  const { from, to, regions, ...forwardedProps } = props;

  const {
    barProps,
    barRole,
    bars,
    barWidth,
    chartHeight,
    disableAnimations,
    gap,
    id,
    labelDecorator,
    margins,
    onBarClick,
    scaleX,
    scaleY,
    tooltipVariant,
    tooltipDecorator,
    xScaleType,
    regions: contextRegions,
  } = useBarChartContext();

  const filteredBarChartBars = bars.filter((bar) => bar.category === bars[0].category);

  const sectionHeight = Math.max(0, Math.min(1, to! - from!)) * chartHeight;
  const sectionY = Math.max(0, Math.min(1, from!)) * chartHeight;

  const constantY = d3scaleLinear().domain(scaleY.domain()).range([sectionHeight, sectionHeight]);

  return (
    <StyledBarChartOverlay
      key={`${id}-series-overlay`}
      aria-hidden="false"
      role="region"
      {...forwardedProps}
      ref={ref as RefObject<SVGGElement>}
      transform={from === 0 && to === 1 ? `translate(${margins.left},${(margins.top || 0) + sectionY})` : undefined}
    >
      {filteredBarChartBars.map((bar, index: number) => {
        const region = (regions || contextRegions)?.find((r) => {
          return xScaleType === 'Date' || xScaleType === 'dateString'
            ? new Date(r.from).getTime() <= (bar.key as Date).getTime() &&
                new Date(r.to).getTime() >= (bar.key as Date).getTime()
            : r.from <= bar.key && r.to >= bar.key;
        });

        const groupedBars = bars.filter((b) => {
          return xScaleType === 'Date' || xScaleType === 'dateString'
            ? (b.key as Date).getTime() === (bar.key as Date).getTime()
            : b.key === bar.key;
        });

        return (
          <Bar
            color="transparent"
            data={{
              data: bar as BarDatum['data'],
            }}
            className="redsift-overlay-bar"
            dataset={bars}
            groupedData={groupedBars}
            disableAnimations={disableAnimations}
            gap={gap}
            id={`id${id}__bar-${index}`}
            index={index}
            key={`bar _.${index}`}
            labelDecorator={labelDecorator}
            maxHeight={0}
            onClick={onBarClick}
            orientation={BarOrientation.vertical}
            direction="down"
            scale={constantY}
            scalePosition={scaleX}
            tooltipVariant={tooltipVariant}
            tooltipDecorator={region && region.tooltipDecorator ? region.tooltipDecorator : tooltipDecorator}
            {...barProps}
            width={barProps?.width && typeof barProps.width === 'number' ? barProps.width : barWidth}
            height={undefined}
            role={barRole}
          />
        );
      })}
    </StyledBarChartOverlay>
  );
});

BarChartOverlay.className = CLASSNAME;
BarChartOverlay.displayName = COMPONENT_NAME;
